import AsyncStorage from '@react-native-async-storage/async-storage';
import { useEffect, useState } from 'react';

const KEY = 'jellyflix.native.preferences.v1';
const BOTTOM_NAV_DEFAULT_MIGRATION_KEY = 'jellyflix.native.bottom-nav-default.v1';

export type MediaSegmentType = 'Intro' | 'Preview' | 'Recap' | 'Commercial' | 'Outro';
export type MediaSegmentAction = 'None' | 'AskToSkip' | 'Skip';
export type MediaSegmentActions = Record<MediaSegmentType, MediaSegmentAction>;
export type PlaybackQualityPolicy = 'original' | 'adaptive';

export const defaultMediaSegmentActions: MediaSegmentActions = {
    Intro: 'AskToSkip',
    Preview: 'None',
    Recap: 'None',
    Commercial: 'None',
    Outro: 'AskToSkip',
};

export type NativePreferences = {
    navigationMode: 'auto' | 'topbar' | 'sidebar';
    audioLanguage: string;
    subtitleLanguage: string;
    subtitleMode: 'Default' | 'Smart' | 'Always' | 'OnlyForced' | 'None';
    keepPreferredTracks: boolean;
    playDefaultAudio: boolean;
    rememberAudioSelections: boolean;
    rememberSubtitleSelections: boolean;
    autoplayNext: boolean;
    reduceMotion: boolean;
    centeredHero: 'auto' | 'on' | 'off';
    compactRows: boolean;
    showContinueWatching: boolean;
    showRecommendations: boolean;
    showGenres: boolean;
    showStreamingServices: boolean;
    playerControls: boolean;
    simplePlaybackRecovery: boolean;
    playbackQuality: PlaybackQualityPolicy;
    playerAutoHideSeconds: number;
    seekBackSeconds: number;
    seekForwardSeconds: number;
    showNextVideoInfoOverlay: boolean;
    showVideoRemainingTime: boolean;
    mediaSegmentActions: MediaSegmentActions;
    playerControlVisibility: Record<string, boolean>;
    bottomNavMobile: boolean;
    mobileEpisodeList: boolean;
    searchCardTitles: boolean;
    webTelevisionMode: boolean;
    uiSounds: boolean;
    controllerHaptics: boolean;
    colorMode: 'dark' | 'light' | 'system';
    serverThemeId: string;
};

export const defaultPreferences: NativePreferences = {
    navigationMode: 'auto',
    audioLanguage: '', subtitleLanguage: '', subtitleMode: 'Default', keepPreferredTracks: true,
    playDefaultAudio: true, rememberAudioSelections: true, rememberSubtitleSelections: true,
    autoplayNext: true, reduceMotion: false, centeredHero: 'auto', compactRows: false, showContinueWatching: true,
    showRecommendations: true, showGenres: true, showStreamingServices: true, playerControls: true,
    // Original fidelity is the default policy; users can opt into automatic
    // source fallback through Adaptive quality or this legacy recovery toggle.
    simplePlaybackRecovery: false,
    playbackQuality: 'original',
    playerAutoHideSeconds: 3,
    seekBackSeconds: 10,
    seekForwardSeconds: 30,
    showNextVideoInfoOverlay: true,
    showVideoRemainingTime: true,
    mediaSegmentActions: defaultMediaSegmentActions,
    playerControlVisibility: {},
    bottomNavMobile: true,
    mobileEpisodeList: true,
    searchCardTitles: false,
    webTelevisionMode: false,
    uiSounds: true,
    controllerHaptics: true,
    colorMode: 'dark',
    serverThemeId: '',
};

export async function readPreferences() {
    const raw = await AsyncStorage.getItem(KEY);
    if (!raw) {
        await AsyncStorage.setItem(BOTTOM_NAV_DEFAULT_MIGRATION_KEY, '1');
        return defaultPreferences;
    }
    try {
        const stored = JSON.parse(raw);
        const legacySeekSeconds = typeof stored.seekSeconds === 'number' ? stored.seekSeconds : undefined;
        const merged = {
            ...defaultPreferences,
            ...stored,
            // Older builds stored this as a boolean. Preserve the user's
            // choice while moving the default to TV-aware Auto behavior.
            centeredHero: stored.centeredHero === true ? 'on' : stored.centeredHero === false ? 'off' : (stored.centeredHero ?? defaultPreferences.centeredHero),
            seekBackSeconds: stored.seekBackSeconds ?? legacySeekSeconds ?? defaultPreferences.seekBackSeconds,
            seekForwardSeconds: stored.seekForwardSeconds ?? legacySeekSeconds ?? defaultPreferences.seekForwardSeconds,
            mediaSegmentActions: {
                ...defaultMediaSegmentActions,
                ...(stored.mediaSegmentActions ?? {}),
            },
            playbackQuality: stored.playbackQuality === 'adaptive' ? 'adaptive' : defaultPreferences.playbackQuality,
        } as NativePreferences;
        if (await AsyncStorage.getItem(BOTTOM_NAV_DEFAULT_MIGRATION_KEY) !== '1') {
            const migrated = { ...merged, bottomNavMobile: true } as NativePreferences;
            await AsyncStorage.multiSet([
                [KEY, JSON.stringify(migrated)],
                [BOTTOM_NAV_DEFAULT_MIGRATION_KEY, '1'],
            ]);
            return migrated;
        }
        return merged;
    } catch {
        return defaultPreferences;
    }
}

type PreferenceListener = (value: NativePreferences) => void;
const preferenceListeners = new Set<PreferenceListener>();
let preferenceCache: NativePreferences | null = null;

export async function savePreferences(value: NativePreferences) {
    preferenceCache = value;
    await AsyncStorage.setItem(KEY, JSON.stringify(value));
    preferenceListeners.forEach((listener) => listener(value));
}

/**
 * React hook returning the current preferences. Updates immediately when
 * preferences are saved from any screen (no polling).
 */
export function useNativePreferences(): NativePreferences {
    const [prefs, setPrefs] = useState<NativePreferences>(preferenceCache ?? defaultPreferences);
    useEffect(() => {
        let mounted = true;
        void readPreferences().then((value) => {
            preferenceCache = value;
            if (mounted) setPrefs(value);
        });
        const listener: PreferenceListener = (value) => { if (mounted) setPrefs(value); };
        preferenceListeners.add(listener);
        return () => { mounted = false; preferenceListeners.delete(listener); };
    }, []);
    return prefs;
}
